import { useState } from 'react';

/**
 * What the user sees of a conversation pane that a remount would lose: where its transcript is
 * scrolled and where the caret is in its composer. Making the other conversation of a split the
 * active one draws both panes again (the active one is the shell's own pane, the other an
 * embedded conversation), so both are read before and put back after.
 */
/** How long a transcript drawn again is held where it was read, while it settles. */
const HOLD_MS = 1500;

export type PaneViewState = {
  /** The transcript's scroll, or `null` when it was at the bottom (it sticks there by itself). */
  scrollTop: number | null;
  /** The caret's offset in the composer's text, when the composer had it. */
  caret: number | null;
};

/** The transcript's scroller: the stick-to-bottom log's own child. */
function scrollerOf(pane: HTMLElement): HTMLElement | null {
  return pane.querySelector<HTMLElement>('[role="log"] > div');
}

/** Its composer, else its first editor (a hidden find field comes before the composer). */
export function composerOf(pane: HTMLElement): HTMLElement | null {
  return (
    pane.querySelector<HTMLElement>('.desktop-composer [contenteditable="true"]') ??
    pane.querySelector<HTMLElement>('[contenteditable="true"], textarea')
  );
}

function caretIn(editor: HTMLElement): number | null {
  if (editor instanceof HTMLTextAreaElement)
    return document.activeElement === editor ? editor.selectionStart : null;
  const selection = window.getSelection();
  if (selection === null || selection.rangeCount === 0) return null;
  const range = selection.getRangeAt(0);
  if (!editor.contains(range.endContainer)) return null;
  const before = document.createRange();
  before.selectNodeContents(editor);
  before.setEnd(range.endContainer, range.endOffset);
  return before.toString().length;
}

function placeCaret(editor: HTMLElement, offset: number): void {
  if (editor instanceof HTMLTextAreaElement) {
    editor.setSelectionRange(offset, offset);
    return;
  }
  const walker = document.createTreeWalker(editor, NodeFilter.SHOW_TEXT);
  let left = offset;
  let node = walker.nextNode();
  while (node !== null) {
    const length = node.textContent?.length ?? 0;
    if (left <= length) {
      const range = document.createRange();
      range.setStart(node, left);
      range.collapse(true);
      const selection = window.getSelection();
      selection?.removeAllRanges();
      selection?.addRange(range);
      return;
    }
    left -= length;
    node = walker.nextNode();
  }
}

/**
 * The caret each conversation's composer last had: a pane that was not the focused one when it
 * was drawn again gets it back the next time it is made the active one.
 */
const lastCarets = new Map<string, number>();

export function readPaneView(pane: HTMLElement | null): PaneViewState {
  if (pane === null) return { scrollTop: null, caret: null };
  const id = pane.dataset['conversationPane'];
  const scroller = scrollerOf(pane);
  const atBottom =
    scroller === null || scroller.scrollHeight - scroller.scrollTop - scroller.clientHeight < 2;
  const composer = composerOf(pane);
  const caret = composer === null ? null : caretIn(composer);
  if (id !== undefined && caret !== null) lastCarets.set(id, caret);
  return {
    scrollTop: atBottom ? null : scroller.scrollTop,
    caret: caret ?? (id === undefined ? null : (lastCarets.get(id) ?? null)),
  };
}

/** Puts the caret back in the (focused) composer and the transcript where it was. */
export function restorePaneView(pane: HTMLElement, state: PaneViewState): void {
  const composer = composerOf(pane);
  if (state.caret !== null && composer?.contains(document.activeElement))
    placeCaret(composer, state.caret);
  const scrollTop = state.scrollTop;
  const id = pane.dataset['conversationPane'];
  if (scrollTop === null) return;
  // The pane may be drawn once more as it settles: the one in the page is held there a moment.
  const current = () =>
    pane.isConnected || id === undefined
      ? pane
      : document.querySelector<HTMLElement>(`[data-conversation-pane="${id}"]`);
  const started = performance.now();
  const hold = () => {
    const shown = current();
    const scroller = shown === null ? null : scrollerOf(shown);
    if (scroller !== null && Math.abs(scroller.scrollTop - scrollTop) > 1)
      scroller.scrollTop = scrollTop;
    if (performance.now() - started < HOLD_MS) requestAnimationFrame(hold);
    else if (id !== undefined) heldScrolls.delete(id);
  };
  hold();
}

/**
 * The transcripts about to be drawn again away from their bottom: drawn, they do not stick to
 * it (`useResumedTranscript`) until their scroll is put back.
 */
const heldScrolls = new Set<string>();

export function holdTranscriptScroll(conversationId: string, state: PaneViewState): void {
  if (state.scrollTop !== null) heldScrolls.add(conversationId);
}

/**
 * The stick-to-bottom start of a conversation's transcript: none when it is drawn again to be
 * scrolled back where it was read (it would glide to its bottom first), else its own.
 */
export function useResumedTranscript(conversationId: string | null): { initial?: false } {
  const [held] = useState(() => conversationId !== null && heldScrolls.has(conversationId));
  return held ? { initial: false } : {};
}

/** Whether the transcript is drawn tall enough to scroll back where it was. */
export function paneViewRestorable(pane: HTMLElement, state: PaneViewState): boolean {
  if (state.scrollTop === null) return true;
  const scroller = scrollerOf(pane);
  return scroller !== null && scroller.scrollHeight - scroller.clientHeight >= state.scrollTop;
}
